/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import { Button, Input, List, Space, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useQuery } from 'react-query';
import {
  getMovementsData,
  getMovementsDataVendors,
} from '~src/services/api/movements';
import IconButton from '~components/_common/IconButton/IconButton';

type CorrectionLetter = {
  id: number;
  title: string;
  description: string;
  status: number;
  movement_ids?: number[];
  comment_ids?: number[];
  question_ids?: number[];
  modification_report?: { file_guid: string; name: string } | null;
  attachments?: { file_guid: string; name: string }[];
};

export type MovementsTabProps = {
  loading: boolean;
  error: boolean;
  isBusy: boolean;

  projectId: number;
  tenderId: number;

  letter?: CorrectionLetter;

  onManageMovements: (
    action: 'add_movement' | 'remove_movement' | 'replace_movement',
    movementIds: number[]
  ) => void;
  readOnly?: boolean;
  shareId?: string;
  shareKey?: string;
};

type LangCode = 'da' | 'en' | 'de' | 'sv';

const normalizeLang = (raw?: string): LangCode => {
  const short = String(raw || '')
    .trim()
    .toLowerCase()
    .slice(0, 2);

  if (short === 'da' || short === 'en' || short === 'de' || short === 'sv') {
    return short;
  }
  return 'en';
};

export const MovementsTab: React.FC<MovementsTabProps> = ({
  loading,
  error,
  isBusy,
  projectId,
  tenderId,
  letter,
  onManageMovements,
  readOnly,
  shareId,
  shareKey,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const isVendorSide = !!shareId && !!shareKey;

  // ==state
  const [searchText, setSearchText] = useState('');

  // ==constants
  const movementIds = useMemo(() => letter?.movement_ids || [], [
    letter?.movement_ids,
  ]);

  const lang = useMemo<LangCode>(() => normalizeLang((intl as any)?.locale), [
    intl,
  ]);

  const movementIdsParam = useMemo(() => {
    if (!movementIds.length) return '';
    return movementIds.join(',');
  }, [movementIds]);

  const selectedMovementsQuery = useQuery<any[]>(
    [
      'movements',
      isVendorSide ? shareId : projectId,
      tenderId,
      movementIdsParam,
      lang,
    ],
    () =>
      isVendorSide
        ? getMovementsDataVendors({
            shareId: shareId!,
            shareKey: shareKey!,
            ids: movementIdsParam,
            lang,
          })
        : getMovementsData({
            project_id: Number(projectId),
            tender_id: Number(tenderId),
            ids: movementIdsParam,
            lang,
          }),
    {
      enabled:
        (isVendorSide ? !!shareId : !!projectId && !!tenderId) &&
        !!movementIdsParam,
      refetchOnWindowFocus: false,
    }
  );

  const selectedMovementsById = useMemo(() => {
    const map = new Map<number, any>();
    (selectedMovementsQuery.data || []).forEach((m) => {
      const id = Number(m?.id);
      if (!Number.isNaN(id)) map.set(id, m);
    });
    return map;
  }, [selectedMovementsQuery.data]);

  // ==filtering
  const filteredMovementIds = useMemo(() => {
    if (!searchText.trim()) return movementIds;

    const searchLower = searchText.toLowerCase();

    return movementIds.filter((id) => {
      const movement = selectedMovementsById.get(Number(id));
      if (!movement) return false;

      // Search in title/head
      const titleText =
        (movement?.summary?.head || '').toLowerCase() +
        ' ' +
        (movement?.custom?.note || '').toLowerCase();
      if (titleText.includes(searchLower)) return true;

      // Search in actions
      const actions = Array.isArray(movement?.summary?.actions)
        ? movement.summary.actions.join(' ').toLowerCase()
        : '';
      if (actions.includes(searchLower)) return true;

      // Search in changes (field names and values)
      const changes = Array.isArray(movement?.changes)
        ? movement.changes
            .map(
              (c) =>
                `${c?.field || ''} ${c?.old_value || ''} ${c?.new_value || ''}`
            )
            .join(' ')
            .toLowerCase()
        : '';
      if (changes.includes(searchLower)) return true;

      // Search in ID
      if (String(id).includes(searchLower)) return true;

      return false;
    });
  }, [movementIds, selectedMovementsById, searchText]);

  // ==return
  if (loading) {
    return (
      <div css={styles.stateBox}>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: 'loading' })}
        </Typography.Text>
      </div>
    );
  }

  if (error || !letter) {
    return (
      <div css={styles.stateBox}>
        <Typography.Text type="danger">
          {intl.formatMessage({ id: 'correction-letter-load-failed' })}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <Typography.Text type="secondary" css={styles.helpText}>
        {intl.formatMessage({ id: 'correction-letter-movements-help' })}
      </Typography.Text>

      {!readOnly && (
        <div css={styles.instructionBox}>
          <Typography.Text type="secondary" css={styles.instructionText}>
            {intl.formatMessage({
              id: 'correction-letter-add-movements-instruction',
            })}
          </Typography.Text>
        </div>
      )}

      <Input
        prefix={<IconButton iconStyle="far" icon="search" backgroundOff />}
        size={'small'}
        placeholder={intl.formatMessage({ id: 'search' })}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        css={styles.searchInput}
      />

      <div css={styles.listSlider}>
        <List
          size="small"
          bordered
          dataSource={filteredMovementIds}
          locale={{
            emptyText: intl.formatMessage({
              id: 'correction-letter-empty-movements',
            }),
          }}
          renderItem={(id) => {
            const movement = selectedMovementsById.get(Number(id));

            const titleText: string =
              movement?.summary?.head || movement?.custom?.note || '';

            const summaryActions: string[] = Array.isArray(
              movement?.summary?.actions
            )
              ? movement.summary.actions
              : [];

            const changesArr: any[] = Array.isArray(movement?.changes)
              ? movement.changes
              : [];

            const hasVisibleChanges =
              summaryActions.length > 0 || changesArr.length > 0;

            return (
              <List.Item
                actions={[
                  !readOnly && (
                    <Button
                      key="rm"
                      size="small"
                      danger
                      disabled={isBusy}
                      onClick={() =>
                        onManageMovements('remove_movement', [id])
                      }>
                      {intl.formatMessage({ id: 'remove' })}
                    </Button>
                  ),
                ]}>
                <Space direction="vertical" size={4}>
                  {!!titleText && (
                    <Typography.Text>{titleText}</Typography.Text>
                  )}

                  {/* Show the change itself */}
                  {hasVisibleChanges && (
                    <div css={styles.movementChangesBox}>
                      {summaryActions.length > 0
                        ? summaryActions.map((t, idx) => (
                            <div
                              key={`${id}_act_${idx}`}
                              css={styles.movementChangeItem}>
                              <Typography.Text type="secondary">
                                {t}
                              </Typography.Text>
                            </div>
                          ))
                        : changesArr.map((c, idx) => {
                            const field = c?.field ? String(c.field) : '';
                            const oldV =
                              c?.old_value === null ||
                              c?.old_value === undefined
                                ? ''
                                : String(c.old_value);
                            const newV =
                              c?.new_value === null ||
                              c?.new_value === undefined
                                ? ''
                                : String(c.new_value);

                            const line =
                              field && (oldV || newV)
                                ? `Field '${field}' changed from '${oldV}' to '${newV}'`
                                : field || oldV || newV || '';

                            return (
                              <div
                                key={`${id}_chg_${idx}`}
                                css={styles.movementChangeItem}>
                                <Typography.Text type="secondary">
                                  {line}
                                </Typography.Text>
                              </div>
                            );
                          })}
                    </div>
                  )}
                </Space>
              </List.Item>
            );
          }}
        />
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingBottom: 18,
    paddingTop: 5,
  },
  stateBox: {
    padding: 16,
    background: '#fafafa',
    borderRadius: 6,
    border: '1px solid #f0f0f0',
  },
  helpText: {
    fontSize: 12,
    lineHeight: 1.35,
  },
  instructionBox: {
    padding: 10,
    backgroundColor: '#f6f8fb',
    border: '1px solid #d9e8f2',
    borderRadius: 4,
    marginBottom: 8,
  },
  instructionText: {
    fontSize: 12,
    lineHeight: 1.4,
  },
  searchInput: {},
  listSlider: {
    maxHeight: 300,
    overflowY: 'auto',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
  },
  movementChangesBox: {
    marginTop: 2,
    paddingLeft: 12,
    borderLeft: '2px solid #e6e6e6',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  movementChangeItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
});
